import {
  SmartToy as AIIcon,
  Send as SendIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Gavel as GavelIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  styled,
} from '@mui/material';
import React, { useState, useEffect } from 'react';

import {
  emailClassificationService,
  EmailClassificationRequest,
  EmailClassificationResult,
  ClassificationHistoryItem,
} from '../../services/email/emailClassificationService';

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: '0.75rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  backgroundColor: '#ffffff',
}));

const ConfidenceBar = styled(LinearProgress)(({ value }: { value: number }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: 'rgba(0, 0, 0, 0.1)',
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    background: value >= 0.8 ? '#4caf50' : value >= 0.6 ? '#ff9800' : '#f44336',
  },
}));

const EmailIntelligencePanel: React.FC = () => {
  const [emailData, setEmailData] = useState<EmailClassificationRequest>({
    content: '',
    sender: '',
    subject: '',
  });
  const [classificationResult, setClassificationResult] =
    useState<EmailClassificationResult | null>(null);
  const [isClassifying, setIsClassifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [classificationHistory, setClassificationHistory] = useState<ClassificationHistoryItem[]>(
    []
  );
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadClassificationHistory();
  }, []);

  const loadClassificationHistory = async () => {
    try {
      const history = await emailClassificationService.getClassificationHistory();
      setClassificationHistory(history);
    } catch (error) {
      console.error('Error loading classification history:', error);
    }
  };

  const handleClassifyEmail = async () => {
    if (!emailData.content || !emailData.sender || !emailData.subject) {
      setError('Please fill in all required fields');
      return;
    }

    setIsClassifying(true);
    setError(null);
    setClassificationResult(null);

    try {
      const result = await emailClassificationService.classifyEmail(emailData);
      setClassificationResult(result);
      // Reload history to include the new classification
      await loadClassificationHistory();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to classify email');
    } finally {
      setIsClassifying(false);
    }
  };

  const handleClearForm = () => {
    setEmailData({
      content: '',
      sender: '',
      subject: '',
    });
    setClassificationResult(null);
    setError(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const content = e.target?.result as string;
        setEmailData(prev => ({ ...prev, content }));
      };
      reader.readAsText(file);
    }
  };

  const getClassificationIcon = (classification: string) => {
    switch (classification) {
      case 'existing_client':
      case 'potential_client':
        return <PersonIcon />;
      case 'vendor':
        return <BusinessIcon />;
      case 'legal_notice':
      case 'court_filing':
        return <GavelIcon />;
      case 'contract_related':
        return <DescriptionIcon />;
      default:
        return <AIIcon />;
    }
  };

  return (
    <StyledCard>
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AIIcon sx={{ color: '#3B82F6', mr: 1 }} />
            <Typography variant="h6" fontWeight="medium">
              Email Intelligence
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh History">
              <IconButton size="small" onClick={loadClassificationHistory}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => setShowHistory(!showHistory)}
              sx={{ ml: 1 }}
            >
              History ({classificationHistory.length})
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          AI-powered email classification and analysis
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Email Input Form */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Classify Email
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Sender Email"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={emailData.sender}
                  onChange={e => setEmailData({ ...emailData, sender: e.target.value })}
                  placeholder="sender@example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Subject"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={emailData.subject}
                  onChange={e => setEmailData({ ...emailData, subject: e.target.value })}
                  placeholder="Email subject line"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email Content"
                  variant="outlined"
                  multiline
                  rows={4}
                  fullWidth
                  value={emailData.content}
                  onChange={e => setEmailData({ ...emailData, content: e.target.value })}
                  placeholder="Paste email content here..."
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={isClassifying ? <CircularProgress size={20} /> : <AIIcon />}
                    onClick={handleClassifyEmail}
                    disabled={
                      isClassifying || !emailData.content || !emailData.sender || !emailData.subject
                    }
                    sx={{
                      backgroundColor: '#3B82F6',
                      '&:hover': { backgroundColor: '#2563EB' },
                    }}
                  >
                    {isClassifying ? 'Classifying...' : 'Classify Email'}
                  </Button>
                  <Button variant="outlined" component="label" disabled={isClassifying}>
                    Upload File
                    <input type="file" hidden accept=".txt,.eml" onChange={handleFileUpload} />
                  </Button>
                  <Button variant="outlined" onClick={handleClearForm} disabled={isClassifying}>
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Classification Results */}
        {classificationResult && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Classification Results
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Classification
                    </Typography>
                    <Chip
                      icon={getClassificationIcon(classificationResult.classification)}
                      label={emailClassificationService.getClassificationLabel(
                        classificationResult.classification
                      )}
                      sx={{
                        backgroundColor: emailClassificationService.getClassificationColor(
                          classificationResult.classification
                        ),
                        color: 'white',
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Confidence
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ConfidenceBar
                        variant="determinate"
                        value={classificationResult.confidence * 100}
                        sx={{ flexGrow: 1 }}
                      />
                      <Typography variant="body2">
                        {Math.round(classificationResult.confidence * 100)}%
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Urgency Level
                    </Typography>
                    <Chip
                      icon={
                        classificationResult.urgency <= 2 ? <WarningIcon /> : <CheckCircleIcon />
                      }
                      label={emailClassificationService.getUrgencyLabel(
                        classificationResult.urgency
                      )}
                      sx={{
                        backgroundColor: emailClassificationService.getUrgencyColor(
                          classificationResult.urgency
                        ),
                        color: 'white',
                      }}
                    />
                  </Box>
                  {classificationResult.deadline && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Deadline
                      </Typography>
                      <Chip
                        icon={<ScheduleIcon />}
                        label={new Date(classificationResult.deadline).toLocaleDateString()}
                        color="warning"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Required Action
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {classificationResult.required_action}
                  </Typography>
                  {classificationResult.reference && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Reference
                      </Typography>
                      <Typography variant="body2">{classificationResult.reference}</Typography>
                    </>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Classification History */}
        {showHistory && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Classification History</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {classificationHistory.length === 0 ? (
                <Typography color="text.secondary" textAlign="center">
                  No classification history found
                </Typography>
              ) : (
                <List>
                  {classificationHistory.map(item => (
                    <ListItem key={item.id} divider>
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: emailClassificationService.getClassificationColor(
                              item.classification
                            ),
                          }}
                        >
                          {getClassificationIcon(item.classification)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={item.subject}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {item.sender_email}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Chip
                                size="small"
                                label={emailClassificationService.getClassificationLabel(
                                  item.classification
                                )}
                                sx={{
                                  backgroundColor:
                                    emailClassificationService.getClassificationColor(
                                      item.classification
                                    ),
                                  color: 'white',
                                }}
                              />
                              <Chip
                                size="small"
                                label={`${Math.round(item.confidence * 100)}% confidence`}
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.created_at).toLocaleDateString()}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </StyledCard>
  );
};

export default EmailIntelligencePanel;
